import React, { createContext, useContext, useState, useEffect } from 'react';
import { Project, PomodoroRecord } from '../types/project';
import { Task } from '../types/task';
import { useAuthState } from '../hooks/useAuthState';
import { showToast } from '@/utils/toast';
import { supabase, handleSupabaseError } from '../config/supabase';
import { mapProjectFromDb, mapTaskFromDb, mapPomodoroFromDb } from '../utils/transforms';

interface ProjectContextType {
  projects: Project[];
  tasks: Task[];
  pomodoros: PomodoroRecord[];
  loading: boolean;
  addProject: (project: Omit<Project, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  updateProject: (id: string, project: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  addTask: (task: Omit<Task, 'id' | 'userId'>) => Promise<Task>;
  updateTask: (id: string, task: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  addPomodoro: (pomodoro: Omit<PomodoroRecord, 'id'>) => Promise<PomodoroRecord>;
  updatePomodoro: (
    id: string | string[], 
    pomodoroData: Partial<PomodoroRecord> | PomodoroRecord[], 
    options?: { silent: boolean }
  ) => Promise<void>;
  deletePomodoro: (id: string | string[]) => Promise<void>;
  reorderTasks: (tasks: Task[]) => Promise<void>;
}

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [pomodoros, setPomodoros] = useState<PomodoroRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthState();

  // One-time localStorage migration: ensure duration is stored in seconds
  useEffect(() => {
    try {
      const raw = localStorage.getItem('pomodoros');
      if (!raw) return;
      const parsed: PomodoroRecord[] = JSON.parse(raw);
      let changed = false;
      const normalized = parsed.map((p) => {
        // If we have both times, derive expected seconds
        if (p.startTime && p.endTime) {
          const expectedSeconds = Math.max(Math.floor((p.endTime - p.startTime) / 1000), 1);
          // Heuristic: if stored duration looks like minutes, convert to seconds
          // e.g., duration is small (<= 300) and 60x matches expectedSeconds closely
          if (p.duration > 0 && p.duration <= 300) {
            const asSeconds = p.duration * 60;
            if (Math.abs(asSeconds - expectedSeconds) <= 180) { // within 3 minutes tolerance
              changed = true;
              return { ...p, duration: asSeconds };
            }
          }
        }
        return p;
      });
      if (changed) {
        localStorage.setItem('pomodoros', JSON.stringify(normalized));
      }
    } catch (_) {
      // Ignore migration errors silently
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Test connection first
        const { error: testError } = await supabase
          .from('projects')
          .select('count')
          .limit(1);
        
        if (testError) {
          console.warn('Supabase connection failed, using localStorage fallback');
          
          // Try to load data from localStorage as fallback
          try {
            const savedProjects = localStorage.getItem('projects');
            const savedTasks = localStorage.getItem('tasks');
            const savedPomodoros = localStorage.getItem('pomodoros');
            
            if (savedProjects) {
              const projects = JSON.parse(savedProjects);
              console.log('🔍 DEBUG: Loaded projects from localStorage:', projects.length);
              setProjects(projects);
            }
            
            if (savedTasks) {
              const tasks = JSON.parse(savedTasks);
              console.log('🔍 DEBUG: Loaded tasks from localStorage:', tasks.length);
              setTasks(tasks);
            }
            
            if (savedPomodoros) {
              const parsed: PomodoroRecord[] = JSON.parse(savedPomodoros);
              console.log('🔍 DEBUG: Loaded pomodoros from localStorage:', parsed.length);
              setPomodoros(parsed);
            }
          } catch (error) {
            console.error('Error loading from localStorage:', error);
            setProjects([]);
            setTasks([]);
            setPomodoros([]);
          }
          
          setLoading(false);
          return;
        }
        
        // One-time migration: push localStorage data to new Supabase if DB is empty
        const migrationKey = 'pomomax_migration_done_v2';
        if (!localStorage.getItem(migrationKey)) {
          const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
          const localTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
          const localPomodoros = JSON.parse(localStorage.getItem('pomodoros') || '[]');

          // Check if Supabase is empty for this user
          const { count } = await supabase.from('pomodoros').select('*', { count: 'exact', head: true }).eq('user_id', user.id);

          if ((count === 0 || count === null) && (localProjects.length > 0 || localTasks.length > 0 || localPomodoros.length > 0)) {
            console.log(`🔄 Migrating localStorage data to Supabase: ${localProjects.length} projects, ${localTasks.length} tasks, ${localPomodoros.length} pomodoros`);

            // Migrate projects
            if (localProjects.length > 0) {
              const projectRows = localProjects.map((p: any) => ({
                id: p.id,
                user_id: user.id,
                name: p.name || 'Untitled',
                description: p.description || null,
                color: p.color || '#3B82F6',
                created_at: p.createdAt || new Date().toISOString(),
              }));
              const { error: pErr } = await supabase.from('projects').upsert(projectRows, { onConflict: 'id' });
              if (pErr) console.error('Migration: projects error', pErr);
              else console.log(`✅ Migrated ${projectRows.length} projects`);
            }

            // Migrate tasks
            if (localTasks.length > 0) {
              const taskRows = localTasks.map((t: any) => ({
                id: t.id,
                user_id: user.id,
                project_id: t.projectId || null,
                title: t.title || 'Untitled',
                description: t.description || null,
                status: t.status || 'pending',
                priority: t.priority || 'medium',
                estimated_pomodoros: t.estimatedPomodoros || 1,
                completed_pomodoros: t.completedPomodoros || 0,
                completed: t.completed || false,
                order_index: t.orderIndex || 0,
                created_at: t.createdAt ? new Date(t.createdAt).toISOString() : new Date().toISOString(),
              }));
              // Insert in batches of 50
              for (let i = 0; i < taskRows.length; i += 50) {
                const batch = taskRows.slice(i, i + 50);
                const { error: tErr } = await supabase.from('tasks').upsert(batch, { onConflict: 'id' });
                if (tErr) console.error('Migration: tasks batch error', tErr);
              }
              console.log(`✅ Migrated ${taskRows.length} tasks`);
            }

            // Migrate pomodoros
            if (localPomodoros.length > 0) {
              const pomRows = localPomodoros.map((p: any) => ({
                id: p.id,
                user_id: user.id,
                project_id: p.projectId || null,
                task_id: p.taskId || null,
                duration: p.duration || 0,
                start_time: p.startTime ? new Date(p.startTime).toISOString() : null,
                end_time: p.endTime ? new Date(p.endTime).toISOString() : null,
                type: p.type || 'work',
                title: p.title || 'Pomodoro Session',
                completed: p.completed ?? true,
                notes: p.notes || null,
                completed_at: p.completedAt || new Date().toISOString(),
              }));
              // Insert in batches of 50
              for (let i = 0; i < pomRows.length; i += 50) {
                const batch = pomRows.slice(i, i + 50);
                const { error: pomErr } = await supabase.from('pomodoros').upsert(batch, { onConflict: 'id' });
                if (pomErr) console.error('Migration: pomodoros batch error', pomErr);
              }
              console.log(`✅ Migrated ${pomRows.length} pomodoros`);
            }

            localStorage.setItem(migrationKey, 'true');
            console.log('🎉 Migration complete!');
          } else {
            localStorage.setItem(migrationKey, 'true');
          }
        }

        // Load projects
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (projectsError) throw projectsError;

        const mappedProjects = (projectsData || []).map(mapProjectFromDb);

        setProjects(mappedProjects);

        // Save to localStorage as backup
        localStorage.setItem('projects', JSON.stringify(mappedProjects));

        // Load tasks
        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (tasksError) throw tasksError;

        const mappedTasks = (tasksData || []).map(mapTaskFromDb);

        setTasks(mappedTasks);

        // Save to localStorage as backup
        localStorage.setItem('tasks', JSON.stringify(mappedTasks));

        // Load pomodoros
        console.log('🔍 DEBUG: Loading pomodoros for user:', user.id);
        
        const { data: pomodorosData, error: pomodorosError } = await supabase
          .from('pomodoros')
          .select('*')
          .eq('user_id', user.id)
          .order('start_time', { ascending: false });

        if (pomodorosError) {
          console.error('Error loading pomodoros:', pomodorosError);
          throw pomodorosError;
        }

        const mappedPomodoros = (pomodorosData || []).map(mapPomodoroFromDb);

        setPomodoros(mappedPomodoros);

        // Save to localStorage as backup
        localStorage.setItem('pomodoros', JSON.stringify(mappedPomodoros));

      } catch (error) {
        console.error('Error loading data:', error);
        // Don't show error toast for connection issues, just use offline mode
        if ((error as any)?.message?.includes('Failed to fetch') || (error as any)?.message?.includes('ERR_NAME_NOT_RESOLVED')) {
          console.warn('Using offline mode due to connection issues');
          setProjects([]);
          setTasks([]);
          setPomodoros([]);
        } else {
          handleSupabaseError(error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.id]); // Only depend on user.id, not the entire user object

  const addProject = async (projectData: Omit<Project, 'id' | 'userId' | 'createdAt'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          name: projectData.name,
          description: projectData.description,
          color: projectData.color,
        })
        .select()
        .single();

      if (error) throw error;

      const mapped = mapProjectFromDb(data);

      setProjects(prev => {
        const newProjects = [mapped, ...prev];
        localStorage.setItem('projects', JSON.stringify(newProjects));
        return newProjects;
      });
      showToast.success('Project created successfully');
    } catch (error) {
      console.error('Error creating project:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const updateProject = async (id: string, projectData: Partial<Project>) => {
    try {
      const payload: Record<string, any> = {};
      if (projectData.name !== undefined) payload.name = projectData.name;
      if (projectData.description !== undefined) payload.description = projectData.description;
      if (projectData.color !== undefined) payload.color = projectData.color;

      const { data, error } = await supabase
        .from('projects')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const mapped = mapProjectFromDb(data);
      setProjects(prev => prev.map(p =>
        p.id === id ? mapped : p
      ));
      showToast.success('Project updated successfully');
    } catch (error) {
      console.error('Error updating project:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast.success('Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const addTask = async (taskData: Omit<Task, 'id' | 'userId'>): Promise<Task> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          user_id: user.id,
          project_id: taskData.projectId || null,
          title: taskData.title,
          description: taskData.description,
          status: taskData.status || 'pending',
          priority: taskData.priority || 'medium',
          due_date: taskData.dueDate,
          estimated_pomodoros: taskData.estimatedPomodoros || 1,
          completed_pomodoros: taskData.completedPomodoros || 0,
          order_index: taskData.orderIndex || 0,
        })
        .select()
        .single();

      if (error) throw error;

      const mapped = mapTaskFromDb(data);

      setTasks(prev => {
        const newTasks = [mapped, ...prev];
        localStorage.setItem('tasks', JSON.stringify(newTasks));
        return newTasks;
      });
      return mapped;
    } catch (error) {
      console.error('Error creating task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const updateTask = async (id: string, taskData: Partial<Task>) => {
    try {
      const payload: Record<string, any> = {};
      if (taskData.title !== undefined) payload.title = taskData.title;
      if (taskData.description !== undefined) payload.description = taskData.description;
      if (taskData.status !== undefined) payload.status = taskData.status;
      if (taskData.priority !== undefined) payload.priority = taskData.priority;
      if (taskData.dueDate !== undefined) payload.due_date = taskData.dueDate;
      if (taskData.estimatedPomodoros !== undefined) payload.estimated_pomodoros = taskData.estimatedPomodoros;
      if (taskData.completedPomodoros !== undefined) payload.completed_pomodoros = taskData.completedPomodoros;
      if (taskData.orderIndex !== undefined) payload.order_index = taskData.orderIndex;
      if (taskData.projectId !== undefined) payload.project_id = taskData.projectId || null;

      const { data, error } = await supabase
        .from('tasks')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const mapped = mapTaskFromDb(data);
      setTasks(prev => prev.map(t =>
        t.id === id ? mapped : t
      ));
    } catch (error) {
      console.error('Error updating task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting task:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const reorderTasks = async (reorderedTasks: Task[]) => {
    try {
      setTasks(reorderedTasks);

      // Persist order_index to DB
      await Promise.all(
        reorderedTasks.map((task, index) =>
          supabase
            .from('tasks')
            .update({ order_index: index })
            .eq('id', task.id)
        )
      );
    } catch (error) {
      console.error('Error reordering tasks:', error);
      throw error;
    }
  };

  const addPomodoro = async (pomodoro: Omit<PomodoroRecord, 'id'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const completedAt = pomodoro.completedAt || new Date().toISOString();

      const insertData: Record<string, any> = {
        user_id: user.id,
        project_id: pomodoro.projectId || null,
        task_id: pomodoro.taskId || null,
        duration: pomodoro.duration,
        notes: pomodoro.notes,
        completed_at: completedAt,
        start_time: pomodoro.startTime ? new Date(pomodoro.startTime).toISOString() : new Date().toISOString(),
        end_time: pomodoro.endTime ? new Date(pomodoro.endTime).toISOString() : new Date().toISOString(),
        type: pomodoro.type || 'work',
        title: pomodoro.title || 'Pomodoro Session',
        completed: pomodoro.completed ?? false,
      };

      const { data, error } = await supabase
        .from('pomodoros')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const pomodoroRecord = mapPomodoroFromDb(data);

      setPomodoros(prev => {
        const newPomodoros = [pomodoroRecord, ...prev];
        localStorage.setItem('pomodoros', JSON.stringify(newPomodoros));
        return newPomodoros;
      });

      return pomodoroRecord;
    } catch (error) {
      console.error('Error recording pomodoro:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const updatePomodoro = async (
    id: string | string[],
    pomodoroData: Partial<PomodoroRecord> | PomodoroRecord[],
    options?: { silent: boolean }
  ) => {
    try {
      const buildPayload = (d: Partial<PomodoroRecord>) => {
        const payload: Record<string, any> = {};
        if (d.duration !== undefined) payload.duration = d.duration;
        if (d.notes !== undefined) payload.notes = d.notes;
        if (d.completedAt !== undefined) payload.completed_at = d.completedAt;
        if (d.projectId !== undefined) payload.project_id = d.projectId;
        if (d.taskId !== undefined) payload.task_id = d.taskId;
        if (d.endTime !== undefined) payload.end_time = new Date(d.endTime).toISOString();
        if (d.startTime !== undefined) payload.start_time = new Date(d.startTime).toISOString();
        if (d.completed !== undefined) payload.completed = d.completed;
        if (d.type !== undefined) payload.type = d.type;
        if (d.title !== undefined) payload.title = d.title;
        return payload;
      };

      if (Array.isArray(id)) {
        for (let i = 0; i < id.length; i++) {
          const updateData = Array.isArray(pomodoroData) ? pomodoroData[i] : pomodoroData;
          const { error } = await supabase
            .from('pomodoros')
            .update(buildPayload(updateData))
            .eq('id', id[i]);
          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from('pomodoros')
          .update(buildPayload(pomodoroData as Partial<PomodoroRecord>))
          .eq('id', id);
        if (error) throw error;
      }

      // Update local state
      if (Array.isArray(id)) {
        setPomodoros(prev => prev.map(p => {
          const updateData = Array.isArray(pomodoroData) ? pomodoroData.find(up => up.id === p.id) : pomodoroData;
          return updateData ? { ...p, ...updateData } : p;
        }));
      } else {
        setPomodoros(prev => prev.map(p =>
          p.id === id ? { ...p, ...(pomodoroData as Partial<PomodoroRecord>) } : p
        ));
      }

      if (!options?.silent) {
        showToast.success(Array.isArray(id)
          ? `Updated ${id.length} pomodoro sessions`
          : 'Pomodoro session updated'
        );
      }
    } catch (error) {
      console.error('Error updating pomodoro:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const deletePomodoro = async (id: string | string[]) => {
    try {
      if (Array.isArray(id)) {
        // Bulk delete
        const { error } = await supabase
          .from('pomodoros')
          .delete()
          .in('id', id);

        if (error) throw error;
        
        setPomodoros(prevPomodoros => 
          prevPomodoros.filter(p => !id.includes(p.id))
        );
      } else {
        // Single delete
        const { error } = await supabase
          .from('pomodoros')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setPomodoros(prevPomodoros => 
          prevPomodoros.filter(p => p.id !== id)
        );
      }
    } catch (error) {
      console.error('Error deleting pomodoro(s):', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  return (
    <ProjectContext.Provider value={{
      projects,
      tasks,
      pomodoros,
      loading,
      addProject,
      updateProject,
      deleteProject,
      addTask,
      updateTask,
      deleteTask,
      addPomodoro,
      updatePomodoro,
      deletePomodoro,
      reorderTasks,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};